import * as THREE from 'three';
import type { Course } from '../course/Course';
import type { Terrain } from '../terrain/Terrain';
import type { MaterialLibrary } from './MaterialLibrary';

/**
 * Small decorative props that give the "rock" and "dirt" materials real
 * usage without inventing geometry unrelated to Course data: rocks
 * scattered around each bunker's own rim (Hole.def.bunkers), and a worn
 * dirt patch at each hole's own tee position (Hole.def.teeBoxes). Neither
 * shape is arbitrary — both are derived directly from region/position data
 * already on Hole. Placement height is sampled from the real terrain
 * (`terrain.getHeightAt`), not assumed from authored data, so props sit on
 * the sculpted ground instead of floating or clipping into it.
 *
 * Rocks are instanced (one InstancedMesh per course, regardless of bunker
 * count) since there's no reason a decorative prop system should cost more
 * draw calls than the vegetation system already avoids paying.
 */
export class PropRenderer {
  private rockMesh: THREE.InstancedMesh | null = null;
  private readonly dirtPatches: THREE.Mesh[] = [];

  render(course: Course, terrain: Terrain, materials: MaterialLibrary, root: THREE.Object3D): void {
    this.renderBunkerRocks(course, terrain, materials, root);
    this.renderTeeDirtPatches(course, terrain, materials, root);
  }

  private renderBunkerRocks(
    course: Course,
    terrain: Terrain,
    materials: MaterialLibrary,
    root: THREE.Object3D,
  ): void {
    const placements: Array<{ x: number; z: number; seed: number }> = [];
    let seed = 401;
    const rnd = (): number => {
      seed = (seed * 1103515245 + 12345) & 0x7fffffff;
      return (seed % 10000) / 10000;
    };

    for (const hole of course.holes) {
      for (const bunker of hole.def.bunkers) {
        if (bunker.kind !== 'circle') continue;
        const rockCount = 3 + Math.floor(rnd() * 3);
        for (let i = 0; i < rockCount; i++) {
          const angle = rnd() * Math.PI * 2;
          const distance = bunker.radius * (1.0 + rnd() * 0.35);
          placements.push({
            x: bunker.center.x + Math.cos(angle) * distance,
            z: bunker.center.z + Math.sin(angle) * distance,
            seed: rnd(),
          });
        }
      }
    }
    if (placements.length === 0) return;

    const geometry = buildRockGeometry();
    const material = materials.surface('rock');
    const mesh = new THREE.InstancedMesh(geometry, material, placements.length);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    mesh.name = 'Props:Rock';

    const matrix = new THREE.Matrix4();
    const quaternion = new THREE.Quaternion();
    const scaleVec = new THREE.Vector3();
    placements.forEach((placement, index) => {
      const scale = 0.28 + placement.seed * 0.65;
      const groundY = terrain.getHeightAt(placement.x, placement.z);
      quaternion.setFromEuler(
        new THREE.Euler(placement.seed * 2, placement.seed * 6, placement.seed * 2),
      );
      scaleVec.setScalar(scale);
      matrix.compose(
        new THREE.Vector3(placement.x, groundY + scale * 0.3, placement.z),
        quaternion,
        scaleVec,
      );
      mesh.setMatrixAt(index, matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;

    root.add(mesh);
    this.rockMesh = mesh;
  }

  private renderTeeDirtPatches(
    course: Course,
    terrain: Terrain,
    materials: MaterialLibrary,
    root: THREE.Object3D,
  ): void {
    for (const hole of course.holes) {
      for (const tee of hole.def.teeBoxes) {
        const geometry = new THREE.CircleGeometry(2.2, 24);
        geometry.rotateX(-Math.PI / 2);
        const mesh = new THREE.Mesh(geometry, materials.surface('dirt'));
        const groundY = terrain.getHeightAt(tee.position.x, tee.position.z);
        mesh.position.set(tee.position.x, groundY + 0.02, tee.position.z);
        mesh.receiveShadow = true;
        mesh.name = `Props:TeeDirt:${hole.number}:${tee.name}`;
        root.add(mesh);
        this.dirtPatches.push(mesh);
      }
    }
  }

  dispose(): void {
    if (this.rockMesh) {
      this.rockMesh.parent?.remove(this.rockMesh);
      this.rockMesh.geometry.dispose();
      this.rockMesh.dispose();
      this.rockMesh = null;
    }
    for (const patch of this.dirtPatches) {
      patch.parent?.remove(patch);
      patch.geometry.dispose();
    }
    this.dirtPatches.length = 0;
  }
}

/** A simple displaced-icosahedron "boulder" — cheap, plausible, no external asset needed. */
function buildRockGeometry(): THREE.BufferGeometry {
  const geometry = new THREE.IcosahedronGeometry(1, 2);
  const position = geometry.attributes.position as THREE.BufferAttribute;
  const vertex = new THREE.Vector3();
  for (let i = 0; i < position.count; i++) {
    vertex.fromBufferAttribute(position, i);
    const displacement = 0.78 + pseudoRandom3D(vertex) * 0.38;
    vertex.multiplyScalar(displacement);
    position.setXYZ(i, vertex.x, vertex.y, vertex.z);
  }
  geometry.computeVertexNormals();
  return geometry;
}

function pseudoRandom3D(v: THREE.Vector3): number {
  const s = Math.sin(v.x * 12.9898 + v.y * 78.233 + v.z * 37.719) * 43758.5453;
  return s - Math.floor(s);
}
